import { useCallback, useEffect, useRef, useState } from 'react';
import { NodeChange, NodeDimensionChange, applyNodeChanges, useReactFlow, useUpdateNodeInternals, ResizeDragEvent } from 'reactflow';
import { getDefaultNodeWidth } from '../utils/nodeHelpers';

export interface NodeResizingConfig {
  // Node identification
  id: string;
  nodeType: string; // Added nodeType
  
  // Size constraints
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
  
  // Content measurement
  contentRef: React.RefObject<HTMLDivElement>;
  
  // Optional callbacks
  onResizeStart?: () => void;
  onResizeComplete?: () => void;
  
  // Optional flags
  lockHeight?: boolean;
  autoAdjustHeight?: boolean;
  
  // Optional custom height calculation
  calculateMinHeight?: (width: number, headerHeight: number) => number;
}

/**
 * Custom hook for handling node resizing with advanced features
 * 
 * Features:
 * - Width and height constraints
 * - Content-aware resizing
 * - Auto height adjustment based on content
 * - Throttled resize updates for performance
 * - Proper cleanup of observers
 */
export function useNodeResizing({
  id,
  nodeType, // Destructure nodeType
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  contentRef,
  onResizeComplete,
  lockHeight = false,
  autoAdjustHeight = true,
  calculateMinHeight,
}: NodeResizingConfig) {
  const { getNode, setNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  
  // Reference to track the last resize timestamp for debouncing
  const lastResizeRef = useRef<number>(0);
  
  // Track the previous width to detect changes that might affect content layout
  const prevWidthRef = useRef<number>(0);
  
  // Get current node dimensions
  const node = getNode(id);
  // Use getDefaultNodeWidth for initial width if node.width is not set
  const currentWidth = node?.width || getDefaultNodeWidth(nodeType);
  const currentHeight = node?.height || minHeight;
  
  // Calculate effective minimum height based on content if needed
  const effectiveMinHeight = calculateMinHeight 
    ? Math.max(minHeight, calculateMinHeight(currentWidth, contentHeight))
    : minHeight;
  
  // Effect to measure content height and set up ResizeObserver
  useEffect(() => {
    // Initial measurement
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }
    
    // Initialize prevWidthRef with current width
    prevWidthRef.current = currentWidth;
    
    // Set up ResizeObserver to detect content size changes in real-time
    if (contentRef.current && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === contentRef.current) {
            setContentHeight(entry.contentRect.height);
          }
        }
      });
      
      resizeObserverRef.current.observe(contentRef.current);
    }
    
    // Cleanup
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [contentRef, currentWidth]);
  
  // Effect to adjust node height based on content requirements (auto-grow and auto-shrink)
  useEffect(() => {
    if (!autoAdjustHeight || lockHeight) return;
    
    const node = getNode(id);
    if (!node) return;

    // If the node is currently being resized by the user, don't auto-adjust
    if (node.resizing) {
      return;
    }

    const currentActualNodeHeight = node.height || 0;
    
    // If current height is less than the effective minimum height, resize to fit content
    if (currentActualNodeHeight < effectiveMinHeight) {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, height: effectiveMinHeight } : n))
      );
      updateNodeInternals(id);
    }
    // If content has been removed/hidden and current height is significantly larger than needed
    // Allow some buffer to prevent constant resizing on small content changes
    else if (currentActualNodeHeight > effectiveMinHeight + 10) {
      // Only auto-shrink if the node wasn't manually resized larger by the user
      // We can determine this by checking if the height is close to a previous effectiveMinHeight value
      // For now, we'll just ensure it's not too much larger than what's needed
      if (currentActualNodeHeight > maxHeight * 0.75 && effectiveMinHeight < maxHeight * 0.5) {
        setNodes((nds) =>
          nds.map((n) => (n.id === id ? { ...n, height: effectiveMinHeight } : n))
        );
        updateNodeInternals(id);
      }
    }
  }, [id, effectiveMinHeight, getNode, setNodes, updateNodeInternals, maxHeight, autoAdjustHeight, lockHeight]);
  
  // Resize handler
  const onResize = useCallback((_: ResizeDragEvent, { width, height }: { width: number; height: number }) => {
    // Calculate new width with constraints
    const newWidth = Math.max(minWidth, Math.min(maxWidth, width));
    
    // Get current node
    const node = getNode(id);
    if (!node) return;
    
    // Detect if width has changed, which might affect content layout
    const widthChanged = Math.abs(prevWidthRef.current - newWidth) >= 1;
    if (widthChanged) {
      prevWidthRef.current = newWidth;
    }
    
    // Calculate new height with constraints
    // If lockHeight is true, keep the original height
    const newHeight = lockHeight 
      ? node.height || minHeight
      : Math.max(effectiveMinHeight, Math.min(maxHeight, height));
    
    // Throttle updates to avoid performance issues
    // Only apply changes if enough time has passed since last update
    const now = Date.now();
    if (now - lastResizeRef.current > 16) { // ~60fps
      lastResizeRef.current = now;
      
      // Apply both width and height in a single update for smoother resizing
      const changes: NodeChange[] = [
        {
          id,
          type: 'dimensions',
          dimensions: { 
            width: newWidth,
            height: newHeight
          },
          resizing: true,
        } as NodeDimensionChange
      ];
      
      setNodes((nodes) => applyNodeChanges(changes, nodes));
      
      // Force a measurement update on the content ref when width changes
      // This ensures content reflow is measured immediately
      if (widthChanged && contentRef.current) {
        // Directly measure and update the content height
        setContentHeight(contentRef.current.offsetHeight);
      }
    }
  }, [id, setNodes, effectiveMinHeight, minWidth, maxWidth, maxHeight, getNode, minHeight, lockHeight]);
  
  // Resize end handler
  const onResizeEnd = useCallback((_: ResizeDragEvent, { width, height }: { width: number; height: number }) => {
    // Calculate final width and height with constraints
    const finalWidth = Math.max(minWidth, Math.min(maxWidth, width));
    
    // If lockHeight is true, keep the original height
    const node = getNode(id);
    const finalHeight = lockHeight
      ? node?.height || minHeight
      : Math.max(effectiveMinHeight, Math.min(maxHeight, height));

    const changes: NodeChange[] = [
      {
        id,
        type: 'dimensions',
        dimensions: {
          width: finalWidth,
          height: finalHeight,
        },
        resizing: false, // Mark resizing as ended
      } as NodeDimensionChange,
    ];
    
    setNodes((nodes) => applyNodeChanges(changes, nodes));
    
    // Force an update of node internals to ensure handles are positioned correctly
    setTimeout(() => {
      updateNodeInternals(id);
    }, 0);
    
    // Call the onResizeComplete callback if provided
    if (onResizeComplete) {
      onResizeComplete();
    }
  }, [id, setNodes, effectiveMinHeight, minWidth, maxWidth, maxHeight, updateNodeInternals, onResizeComplete, getNode, minHeight, lockHeight]);

  return {
    onResize,
    onResizeEnd,
    currentWidth,
    currentHeight,
    effectiveMinHeight,
    contentHeight
  };
}
